import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useRef, useState} from 'react'

import {AlertsSidePanel} from './AlertsSidePanel'
import {useAnnotations} from '../../stores/selectors'
import type {PageLimits} from '../../page-data/payloads/files'
import {usePullRequestToolbarAnalytics} from '../../hooks/use-pull-request-toolbar-analytics'

export interface OpenAlertsPanelButtonProps {
  isFileTreeExpanded?: boolean
  pageLimits: PageLimits
}

/**
 *
 * Renders a button that opens the annotations side panel
 * Contains the total number of annotations plus the icon
 */
export function OpenAlertsPanelButton({isFileTreeExpanded, pageLimits}: OpenAlertsPanelButtonProps) {
  const [sidePanelIsOpen, setSidePanelIsOpen] = useState(false)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)

  const handleClose = useCallback(() => setSidePanelIsOpen(false), [])

  const annotationsById = useAnnotations()
  const {sendPullRequestAnalyticsEvent} = usePullRequestToolbarAnalytics()

  if (!annotationsById) {
    return null
  }

  const annotations = Object.values(annotationsById)

  const totalAnnotationsCount = annotations.length

  return totalAnnotationsCount > 0 ? (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        fallback={
          <Button
            aria-label="The alerts side panel cannot currently be opened."
            icon={AlertIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Button
          ref={toggleSidePanelRef}
          aria-label="Open alerts side panel"
          count={totalAnnotationsCount}
          leadingVisual={AlertIcon}
          size="small"
          onClick={() => {
            sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
            ensurePreviousActiveDialogIsClosed()
            setSidePanelIsOpen(true)
          }}
        >
          {isFileTreeExpanded && <span className="d-none d-xl-block">Alerts</span>}
        </Button>
        <AlertsSidePanel
          annotations={annotations}
          isOpen={sidePanelIsOpen}
          pageLimits={pageLimits}
          returnFocusRef={toggleSidePanelRef}
          onClose={handleClose}
        />
      </ErrorBoundary>
    </div>
  ) : null
}

try{ OpenAlertsPanelButton.displayName ||= 'OpenAlertsPanelButton' } catch {}